import { Skeleton } from '@mui/material';
import { set } from 'lodash';
import { lazy, memo, Suspense } from 'react';
import { useCleanUpDefaultFonts } from 'src/hooks/useCleanUpDefaultFonts';

import { GCFErrorBoundary } from '../errors/GCFErrorBoundary';
import { GCFConfigProvider } from '../gcfConfigProvider';
import { GcfComponentScreenProps } from './GcfComponentScreen.interface';
import { GCFComponentScreenWrapper } from './GcfComponentScreenWrapper';

const Screen = lazy(() =>
  import('gcf-components/Components').then((module) => ({
    default: module.default.Screen,
  })),
);

export const GcfComponentScreen = memo(
  ({
    name,
    width,
    height,
    sequence,
    sections,
    scale,
    testid,
    productFilters,
    productVariationFilters,
    skipProductsApiOnEmptyFilters,
  }: GcfComponentScreenProps) => {
    useCleanUpDefaultFonts();

    const updatedSections = sections.map((section) => {
      for (const assemblyComponent of section?.assemblyComponents ?? []) {
        if (!assemblyComponent?.component?.theme) {
          set(
            assemblyComponent,
            'component.theme',
            assemblyComponent?.component?.config?.scaleTypeKey ?? 'default',
          );
        }
      }

      return section;
    });

    return (
      <GCFComponentScreenWrapper width={width} height={height} scale={scale}>
        <GCFErrorBoundary>
          <Suspense
            fallback={
              <Skeleton
                data-testid={testid && `skeleton-${testid}`}
                height="100%"
                width="100%"
                variant="rectangular"
              />
            }>
            <GCFConfigProvider
              productFilters={productFilters}
              productVariationFilters={productVariationFilters}
              skipProductsApiOnEmptyFilters={skipProductsApiOnEmptyFilters}>
              <Screen
                width={width}
                height={height}
                name={name}
                sequence={sequence}
                sections={updatedSections}
                data-testid={testid}
              />
            </GCFConfigProvider>
          </Suspense>
        </GCFErrorBoundary>
      </GCFComponentScreenWrapper>
    );
  },
);
